import { ColumnDef, Row } from '@tanstack/react-table';
import { addPatient } from '../../types/patient.types';
import { Link } from 'react-router-dom';
import { EyeIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ActionsCell = ({ row }: { row: Row<addPatient> }) => {
    return (
        <div className="text-center">
            <Link to={`/checkup/${row.original.id}`}>
                <button className="text-primary hover:text-primary/80" type="button">
                    <EyeIcon className="text-primery-blue-400" />
                </button>
            </Link>
        </div>
    );
};

const headerClass =
    'text-sm px-4 py-3 !text-primery-blue-500 leading-[26.24px] font-semibold tracking-normal';
const cellClass = 'text-sm text-[#625E74] font-normal tracking-normal leading-[26.4px]';

export function usePatientColumns(): ColumnDef<addPatient>[] {
    const { t } = useTranslation();

    return [
        {
            accessorKey: 'id',
            header: () => <div className={headerClass}>ID</div>,
            cell: ({ row }) => (
                <div className="text-sm px-4 py-3 !text-primery-blue-500 leading-[26.24px] font-semibold tracking-normal">
                    {row.original.id}
                </div>
            ),
        },
        {
            accessorKey: 'patient_name',
            id: 'name',
            header: () => <div className={headerClass}>{t('patient_name')}</div>,
            cell: ({ row }) => {
                const raw = row.original as addPatient & { patient_name?: string };
                const name =
                    raw.patient_name ??
                    ([raw.first_name, raw.last_name].filter(Boolean).join(' ').trim() || '—');
                return (
                    <div className="text-sm px-4 py-3 !text-primery-blue-500 leading-[26.24px] font-semibold tracking-normal">
                        {name}
                    </div>
                );
            },
        },
        {
            accessorKey: 'patient_phone',
            header: () => <div className={headerClass}>{t('booking_phone')}</div>,
            cell: ({ row }) => {
                const raw = row.original as addPatient & { patient_phone?: string; patient_address?: string };
                const phone = raw.patient_phone ?? raw.phone;
                return <div className={cellClass}>{phone ?? '—'}</div>;
            },
        },
        {
            accessorKey: 'patient_address',
            header: () => <div className={headerClass}>{t('booking_address')}</div>,
            cell: ({ row }) => {
                const raw = row.original as addPatient & { patient_phone?: string; patient_address?: string };
                const address = raw.patient_address ?? raw.address;
                return <div className={cellClass}>{address ?? '—'}</div>;
            },
        },
        {
            accessorKey: 'service_type',
            header: () => <div className={headerClass}>{t('service_type')}</div>,
            cell: ({ row }) => {
                const raw = row.original as addPatient & { service_type?: string };
                const st = raw.service_type ?? '';
                const label =
                    st === 'medical-examination'
                        ? t('service_type_medical_examination', 'فحص طبي')
                        : st === 'follow-up'
                          ? t('service_type_follow_up', 'متابعة')
                          : st === 'consultation'
                            ? t('consultation', 'استشارة')
                            : st || '—';
                return <div className={cellClass}>{label}</div>;
            },
        },
        {
            accessorKey: 'status',
            header: () => <div className={headerClass}>{t('booking_status')}</div>,
            cell: ({ row }) => {
                const status = row.original.status || (row.original as { type?: string }).type || '';
                let statusStyles: string;
                let statusText: string;

                switch (status) {
                    case 'waiting':
                        statusStyles = 'text-amber-600 font-bold bg-amber-100';
                        statusText = t('waiting', 'في الانتظار');
                        break;
                    case 'inside':
                        statusStyles = 'text-blue-600 font-bold bg-blue-100';
                        statusText = t('inside', 'داخل');
                        break;
                    case 'completed':
                        statusStyles = 'text-green-600 font-bold bg-green-100';
                        statusText = t('completed', 'مكتمل');
                        break;
                    case 'pending':
                        statusStyles = 'text-gray-700 font-bold bg-gray-100';
                        statusText = t('pending', 'قيد الانتظار');
                        break;
                    default:
                        statusStyles = 'text-gray-600 font-bold bg-gray-100';
                        statusText = status || '—';
                }

                return (
                    <div
                        className={`${cellClass} ${statusStyles} w-fit px-[6px] py-1 rounded-sm`}
                    >
                        {statusText}
                    </div>
                );
            },
        },
        {
            id: 'actions',
            header: () => (
                <div className={`text-center ${headerClass}`}>{t('patient_view', 'مشاهدة')}</div>
            ),
            cell: ({ row }) => <ActionsCell row={row} />,
        },
    ];
}
